import {
  BufferGeometry,
  ConeGeometry,
  CylinderGeometry,
  DodecahedronGeometry,
  SphereGeometry,
  TorusGeometry,
} from "three";
import { mergeGeometries } from "three/examples/jsm/utils/BufferGeometryUtils.js";

import type { HairStyle, Headwear } from "../../sim/appearance.ts";

type VisibleHairStyle = Exclude<HairStyle, "bald">;
type VisibleHeadwear = Exclude<Headwear, "none">;

export interface CharacterFeatureGeometrySet {
  readonly hair: Readonly<Record<VisibleHairStyle, BufferGeometry>>;
  readonly headwear: Readonly<Record<VisibleHeadwear, BufferGeometry>>;
}

interface Placement {
  readonly x?: number;
  readonly y?: number;
  readonly z?: number;
  readonly scaleX?: number;
  readonly scaleY?: number;
  readonly scaleZ?: number;
  readonly rotateX?: number;
  readonly rotateY?: number;
  readonly rotateZ?: number;
}

function place(
  geometry: BufferGeometry,
  placement: Readonly<Placement> = {},
): BufferGeometry {
  geometry.scale(
    placement.scaleX ?? 1,
    placement.scaleY ?? 1,
    placement.scaleZ ?? 1,
  );
  geometry.rotateX(placement.rotateX ?? 0);
  geometry.rotateY(placement.rotateY ?? 0);
  geometry.rotateZ(placement.rotateZ ?? 0);
  geometry.translate(
    placement.x ?? 0,
    placement.y ?? 0,
    placement.z ?? 0,
  );
  return geometry;
}

function merge(
  geometries: readonly BufferGeometry[],
  name: string,
): BufferGeometry {
  const compatible = geometries.map((geometry) =>
    geometry.index === null ? geometry : geometry.toNonIndexed()
  );
  const geometry = mergeGeometries(compatible, false);
  if (geometry === null) {
    throw new Error("Could not merge " + name + " character geometry");
  }
  geometry.name = name;
  for (const source of geometries) {
    source.dispose();
  }
  for (const normalized of compatible) {
    if (!geometries.includes(normalized)) {
      normalized.dispose();
    }
  }
  return geometry;
}

function cap(
  radius: number,
  depthRadians: number,
  name: string,
): BufferGeometry {
  const geometry = new SphereGeometry(
    radius,
    10,
    7,
    0,
    Math.PI * 2,
    0,
    depthRadians,
  );
  geometry.name = name;
  return geometry;
}

function createHairGeometry(
  headRadius: number,
): Readonly<Record<VisibleHairStyle, BufferGeometry>> {
  const cropped = place(
    cap(headRadius * 1.025, Math.PI * 0.54, "hair:cropped"),
    { y: headRadius * 0.18, scaleZ: 1.02 },
  );
  const parted = merge([
    place(cap(headRadius * 1.035, Math.PI * 0.58, "parted-cap"), {
      y: headRadius * 0.17,
      x: headRadius * 0.025,
    }),
    place(new DodecahedronGeometry(headRadius * 0.43, 0), {
      x: -headRadius * 0.46,
      y: headRadius * 0.19,
      z: headRadius * 0.72,
      scaleX: 0.52,
      scaleY: 0.82,
      scaleZ: 0.32,
      rotateZ: 0.24,
    }),
  ], "hair:parted");
  const bob = merge([
    place(cap(headRadius * 1.055, Math.PI * 0.71, "bob-cap"), {
      y: headRadius * 0.12,
      scaleZ: 1.04,
    }),
    place(new SphereGeometry(headRadius * 0.52, 8, 5), {
      x: -headRadius * 0.78,
      y: -headRadius * 0.2,
      z: -headRadius * 0.03,
      scaleX: 0.72,
      scaleY: 1.18,
      scaleZ: 0.8,
    }),
    place(new SphereGeometry(headRadius * 0.52, 8, 5), {
      x: headRadius * 0.78,
      y: -headRadius * 0.2,
      z: -headRadius * 0.03,
      scaleX: 0.72,
      scaleY: 1.18,
      scaleZ: 0.8,
    }),
  ], "hair:bob");
  const bun = merge([
    place(cap(headRadius * 1.025, Math.PI * 0.58, "bun-cap"), {
      y: headRadius * 0.16,
    }),
    place(new DodecahedronGeometry(headRadius * 0.49, 0), {
      y: headRadius * 0.96,
      z: -headRadius * 0.54,
      scaleZ: 0.88,
    }),
  ], "hair:bun");
  return { cropped, parted, bob, bun };
}

function createHeadwearGeometry(
  headRadius: number,
): Readonly<Record<VisibleHeadwear, BufferGeometry>> {
  const knitCap = merge([
    place(cap(headRadius * 1.08, Math.PI * 0.58, "knit-cap-dome"), {
      y: headRadius * 0.24,
    }),
    place(new TorusGeometry(headRadius * 0.91, headRadius * 0.075, 4, 10), {
      y: headRadius * 0.21,
      rotateX: Math.PI * 0.5,
    }),
    place(new DodecahedronGeometry(headRadius * 0.17, 0), {
      y: headRadius * 1.15,
    }),
  ], "headwear:knit_cap");
  const flatCap = merge([
    place(new SphereGeometry(headRadius * 1.03, 9, 5), {
      y: headRadius * 0.45,
      z: headRadius * 0.02,
      scaleY: 0.43,
      scaleZ: 1.08,
    }),
    place(new SphereGeometry(headRadius * 0.74, 8, 4), {
      y: headRadius * 0.3,
      z: headRadius * 0.74,
      scaleX: 1.22,
      scaleY: 0.2,
      scaleZ: 0.62,
    }),
  ], "headwear:flat_cap");
  const headscarf = merge([
    place(new SphereGeometry(
      headRadius * 1.075,
      10,
      7,
      Math.PI * 0.7,
      Math.PI * 1.6,
      0,
      Math.PI * 0.78,
    ), {
      y: headRadius * 0.14,
      z: -headRadius * 0.015,
      scaleZ: 1.04,
    }),
    place(new DodecahedronGeometry(headRadius * 0.25, 0), {
      y: -headRadius * 0.2,
      z: -headRadius * 1.02,
      scaleX: 1.15,
      scaleY: 0.85,
      scaleZ: 0.82,
    }),
    place(new ConeGeometry(headRadius * 0.18, headRadius * 0.72, 5), {
      x: -headRadius * 0.18,
      y: -headRadius * 0.48,
      z: -headRadius * 0.84,
      rotateZ: -0.22,
    }),
    place(new ConeGeometry(headRadius * 0.18, headRadius * 0.67, 5), {
      x: headRadius * 0.18,
      y: -headRadius * 0.45,
      z: -headRadius * 0.86,
      rotateZ: 0.25,
    }),
  ], "headwear:headscarf");
  return {
    knit_cap: knitCap,
    flat_cap: flatCap,
    headscarf,
  };
}

export function createCharacterFeatureGeometrySet(
  headRadius: number,
): CharacterFeatureGeometrySet {
  return {
    hair: createHairGeometry(headRadius),
    headwear: createHeadwearGeometry(headRadius),
  };
}

export function disposeCharacterFeatureGeometrySet(
  features: Readonly<CharacterFeatureGeometrySet>,
): void {
  const geometries = new Set<BufferGeometry>([
    ...Object.values(features.hair),
    ...Object.values(features.headwear),
  ]);
  for (const geometry of geometries) {
    geometry.dispose();
  }
}
